import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedInlineText } from './formatted-inline-text';
import { Stamp, type StampProps } from './stamp';
import { splitGameOverText } from '../systems/game-over-text';
import { isMotionReduced } from '../systems/accessibility-settings';
import styles from './game-over-screen.module.css';

export interface GameOverScreenProps {
  heading?: string;
  text: string;
  restartLabel?: string;
  stamp?: Pick<StampProps, 'text' | 'sub'>;
  background?: string;
  onRestart: () => void;
}

export const GameOverScreen = memo(function GameOverScreen({
  heading = 'GAME OVER',
  text,
  restartLabel = 'Recommencer',
  stamp,
  background,
  onRestart,
}: GameOverScreenProps) {
  const [instant, setInstant] = useState(isMotionReduced);
  const { subtitle, body } = useMemo(() => splitGameOverText(text), [text]);
  const stampData = stamp ?? { text: 'GAME OVER' };

  useEffect(() => {
    setInstant(isMotionReduced());
  }, [heading, text, stamp?.text, stamp?.sub, background]);

  const handleOverlayClick = useCallback(() => {
    setInstant(true);
  }, []);

  const handleRestartClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onRestart();
    },
    [onRestart],
  );

  const rootClass = [styles.overlay, instant ? styles.instant : styles.play].join(' ');

  return (
    <div
      className={rootClass}
      role="dialog"
      aria-modal="true"
      aria-labelledby="game-over-heading"
      onClick={handleOverlayClick}
    >
      {background ? (
        <div
          className={styles.background}
          style={{ backgroundImage: `url(${background})` }}
          aria-hidden
        />
      ) : null}
      <div className={styles.veil} aria-hidden />

      <div className={`${styles.stampWrap} cn-zoom-ui`}>
        <Stamp
          text={stampData.text}
          sub={stampData.sub}
          tone="victim"
          drop={!instant}
          dropDelayMs={instant ? 0 : 1000}
          instant={instant}
        />
      </div>

      <div className={`${styles.bottom} cn-zoom-ui`}>
        <div className={styles.card}>
          <h1 id="game-over-heading" className={`${styles.heading} ${styles.seqHeading}`}>
            <FormattedInlineText text={heading} />
          </h1>
          {subtitle ? (
            <p className={`${styles.subtitle} ${styles.seqSubtitle}`}>
              <FormattedInlineText text={subtitle} />
            </p>
          ) : null}
          {body ? (
            <p className={`${styles.body} ${styles.seqBody}`}>
              <FormattedInlineText text={body} />
            </p>
          ) : null}
        </div>
        <button
          type="button"
          className={`${styles.continueBtn} ${styles.seqButton}`}
          onClick={handleRestartClick}
        >
          {restartLabel}
        </button>
      </div>
    </div>
  );
});
